import useElement from "backend/hooks/elements/useElement";
import { ICardElementWithChildren } from "backend/model/model";
import PerformanceTrend from "components/PerformanceTrend";
import { PerformanceDonut } from "design/components/charts/PerformanceDonut";
import ProgressBar2 from "design/components/progress/PercentageBar";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React from "react";
import DomElement from "reportengine/DomElement";
import Portion from "reportengine/Portion";
import Slide from "reportengine/Slide";
import Text from "reportengine/Text";
import TextElement from "reportengine/TextElement";
import { formatDate, getDateFromMEAValue } from "utils/utils";
import ReportComments from "../components/ReportComment";

const InitiativeSlide = (
  props: React.PropsWithChildren<{
    item: ICardElementWithChildren;
    translatedScorecardName?: string;
  }>
) => {
  const { item } = props;
  const ctx = useAppContext();
  const { data: parentElement } = useElement(item.parent_element_id);
  const initSett = conf.getIniativeDetailsSlideGaugeSettings(item);
  const translatedName = getTranslatedAttribute(item, "Name", ctx.language);
  const translatedParentName = getTranslatedAttribute(
    parentElement,
    "Name",
    ctx.language
  );
  const sign = Number(item?.SLIPPAGE) > 0 ? "+" : "-";
  const slippageNumber = Number(item?.SLIPPAGE);
  const slippageText = isNaN(slippageNumber)
    ? ""
    : Math.abs(slippageNumber).toFixed(1);
  const startDate = formatDate(
    getDateFromMEAValue(item.START_DATE),
    t("configtypes.en-GB"),
    true
  );
  const endDate = formatDate(
    getDateFromMEAValue(item.END_DATE),
    t("configtypes.en-GB"),
    true
  );
  return (
    <>
      <Slide index={9}>
        <TextElement slideElement="footer">
          {props.translatedScorecardName} ({t("configtypes.Initiative")})
        </TextElement>
        <Text slideElement="period">
          <Portion paragraphIndex={0} portionIndex={0}>
            {item.period?.name || ""}
          </Portion>
        </Text>

        <ReportComments
          item={item}
          slideElement="justification_text"
          justificationDateElement="justification_date"
          justificationOwnerElement="justification_owner"
          justificationOwnerNameElement="justification_owner_name"
        />

        {item.SLIPPAGE ? (
          <TextElement slideElement="slippage_status">
            {Number(item.SLIPPAGE) > 0
              ? t("strategyReport.Behind Schedule")
              : t("strategyReport.Ahead of Schedule")}
          </TextElement>
        ) : null}

        <Text slideElement="start_and_end_date">
          <Portion portionIndex={0} paragraphIndex={0}>
            {`${t("strategyReport.Start date")}`} &nbsp;
          </Portion>
          <Portion portionIndex={1} paragraphIndex={0}>
            {`${startDate}– `}
          </Portion>
          <Portion portionIndex={2} paragraphIndex={0}>
            {t("strategyReport.End date") } &nbsp;
          </Portion>
          <Portion portionIndex={3} paragraphIndex={0}>
            {`${endDate}`}
          </Portion>
        </Text>
        <TextElement slideElement="header">{translatedParentName}</TextElement>
        <Text slideElement="budget_util">
          <Portion portionIndex={0} paragraphIndex={0}>
            {t("strategyReport.Budget Utilization")}
          </Portion>
          <Portion portionIndex={2} paragraphIndex={0}>
            {item?.period?.name}
          </Portion>
        </Text>
        <Text slideElement="variance">
          <Portion portionIndex={0} paragraphIndex={0}>
            {item[initSett.fields?.[0].attribute as keyof ICardElementWithChildren]}
          </Portion>
          <Portion portionIndex={0} paragraphIndex={1}>
            {t("strategyReport." + initSett.fields?.[0]?.title)}
          </Portion>
        </Text>
        <Text slideElement="planned_progress">
          <Portion portionIndex={0} paragraphIndex={0}>
            {item[initSett.fields?.[1].attribute as keyof ICardElementWithChildren]}
          </Portion>
          <Portion portionIndex={0} paragraphIndex={1}>
            {t("strategyReport." + initSett.fields?.[1]?.title)}
          </Portion>
        </Text>
        <Text slideElement="estimated_end_date">
          <Portion portionIndex={0} paragraphIndex={0}>
            {endDate}
          </Portion>
          <Portion portionIndex={1} paragraphIndex={0}></Portion>
          <Portion portionIndex={2} paragraphIndex={0}></Portion>
        </Text>
        <Text slideElement="ytd_expenditure">
          <Portion portionIndex={0} paragraphIndex={0}>
            {t("strategyReport." + initSett.fields?.[2]?.title)}
          </Portion>
          <Portion portionIndex={3} paragraphIndex={0}>
            {item[initSett.fields?.[2].attribute as keyof ICardElementWithChildren]}
          </Portion>
        </Text>
        <Text slideElement="slippage_duration">
          <Portion portionIndex={0} paragraphIndex={0}>
            {sign}
          </Portion>
          <Portion portionIndex={1} paragraphIndex={0}>
            {slippageText}
          </Portion>
        </Text>
        <TextElement slideElement="name">{translatedName}</TextElement>
        <Text slideElement="total_budget">
          <Portion portionIndex={0} paragraphIndex={0}>
            {t("strategyReport." + initSett.fields?.[3]?.title)}
          </Portion>
          <Portion portionIndex={2} paragraphIndex={0}>
            {item[initSett.fields?.[3].attribute as keyof ICardElementWithChildren]}
          </Portion>
        </Text>
        <TextElement slideElement="progress_title">
          {t("strategyReport.Progress")} {item.period?.name}
        </TextElement>
        <TextElement slideElement="slippage_title">
          {t("strategyReport.Slippage")}
        </TextElement>
        <TextElement slideElement="estimated_end_date_title">
          {t("strategyReport.Estimated End Date")}
        </TextElement>
        <TextElement slideElement="performance_title">
          {t("strategyReport.Performance")}
        </TextElement>
        <TextElement slideElement="progress_trend">
          {t("strategyReport.Progress Trend")}
        </TextElement>
        <DomElement
          typename="image"
          selector=".element"
          slideElement="budget_util_chart"
          transform={{
            x: -20,
            width: 240,
            height: 240,
          }}
        >
          <div className="element" style={{ width: "300px", height: "300px" }}>
            <PerformanceDonut
              element={item}
              settings={conf.getInitiativeDetailsViewGaugeSettings(item)}
              disableAnimation
            />
          </div>
        </DomElement>

        <DomElement
          typename="image"
          selector=".element"
          slideElement="progress_bar"
          transform={{}}
        >
          <div className="element" style={{ width: "300px", height: "60px" }}>
            <ProgressBar2
              color={`#${item.rangecolor}`}
              value={Number(item[initSett.fields?.[4].attribute as keyof ICardElementWithChildren])}
            />
            {/*                
            <ProgressBar
              value={Number(item.COMPLETION_PERCENTAGE)}
              title={item.COMPLETION_PERCENTAGE_prettyvalue}
              bgColor={`#${item.rangecolor}33`}
              barColor={`#${item.rangecolor}ff`}
            />

        */}
          </div>
        </DomElement>
        <DomElement
          typename="image"
          selector=".highcharts-root"
          slideElement="progress_trend_chart"
          transform={{
            y: 70,
            height: -20,
            width: -30,
          }}
        >
          <div style={{ width: "1090px", height: "300px" }}>
            <PerformanceTrend
              disableAnimation
              width="1090px"
              element={item}
              settings={conf.getInitiativeDetailsViewChartSettings(item)}
              showLegend={true}
            />
          </div>
        </DomElement>
      </Slide>
    </>
  );
};

export default InitiativeSlide;
